import React, { useState } from 'react';
import { Download, Calendar, TrendingUp, Users, DollarSign, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { mockEmployees, mockTransactions, mockDashboardStats } from '../data/mockData';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const Reports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('financial');
  const [dateRange, setDateRange] = useState<string>('30d');

  // Department analysis data
  const departmentData = [
    { name: 'Engineering', employees: 1, totalCredit: 10000, avgCredit: 10000, totalSpend: 450 },
    { name: 'Marketing', employees: 1, totalCredit: 15000, avgCredit: 15000, totalSpend: 500 },
    { name: 'Sales', employees: 1, totalCredit: 5000, avgCredit: 5000, totalSpend: 2000 },
    { name: 'HR', employees: 1, totalCredit: 20000, avgCredit: 20000, totalSpend: 75 },
    { name: 'Finance', employees: 1, totalCredit: 8000, avgCredit: 8000, totalSpend: 0 },
  ];

  // Monthly transaction trends
  const monthlyData = [
    { month: 'Jan', transactions: 12, amount: 15000 },
    { month: 'Feb', transactions: 18, amount: 22000 },
    { month: 'Mar', transactions: 15, amount: 18000 },
    { month: 'Apr', transactions: 22, amount: 25000 },
    { month: 'May', transactions: 28, amount: 32000 },
    { month: 'Jun', transactions: 35, amount: 40000 },
  ];

  // Credit utilization by department
  const creditUtilizationData = [
    { name: 'Engineering', used: 3500, limit: 10000, percentage: 35 },
    { name: 'Marketing', used: 7200, limit: 15000, percentage: 48 },
    { name: 'Sales', used: 0, limit: 5000, percentage: 0 },
    { name: 'HR', used: 8900, limit: 20000, percentage: 44.5 },
    { name: 'Finance', used: 0, limit: 8000, percentage: 0 },
  ];

  const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const handleExport = (type: string) => {
    alert(`Exporting ${type} report as CSV/Excel file`);
  };

  const getReportContent = () => {
    switch (selectedReport) {
      case 'financial':
        return (
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Wallet Balance</p>
                    <p className="text-2xl font-bold text-gray-900">₦{mockDashboardStats.totalWalletBalance.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Credit Limit</p>
                    <p className="text-2xl font-bold text-gray-900">₦{mockDashboardStats.totalCreditLimit.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Credit Utilization</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {((mockDashboardStats.totalCreditUsed / mockDashboardStats.totalCreditLimit) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Transaction Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`₦${value}`, 'Amount']} />
                    <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Credit Utilization by Department</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={creditUtilizationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`₦${value}`, 'Amount']} />
                    <Bar dataKey="used" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'department':
        return (
          <div className="space-y-6">
            {/* Department Overview */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Analysis</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">Department</th>
                      <th className="table-header">Employees</th>
                      <th className="table-header">Total Credit</th>
                      <th className="table-header">Avg Credit</th>
                      <th className="table-header">Total Spend</th>
                      <th className="table-header">Utilization</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {departmentData.map((dept) => (
                      <tr key={dept.name} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-gray-900">{dept.employees}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">₦{dept.totalCredit.toLocaleString()}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-gray-900">₦{dept.avgCredit.toLocaleString()}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">₦{dept.totalSpend.toLocaleString()}</span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-primary-600 h-2 rounded-full" 
                                style={{ width: `${(dept.totalSpend / dept.totalCredit) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {((dept.totalSpend / dept.totalCredit) * 100).toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Department Distribution Chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Distribution by Department</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={departmentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="employees"
                  >
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center space-x-4 mt-4">
                {departmentData.map((dept, index) => (
                  <div key={dept.name} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: pieColors[index % pieColors.length] }}
                    ></div>
                    <span className="text-sm text-gray-600">{dept.name}: {dept.employees}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'employee':
        return (
          <div className="space-y-6">
            {/* Employee Performance */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Credit Performance</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">Employee</th>
                      <th className="table-header">Department</th>
                      <th className="table-header">Credit Limit</th>
                      <th className="table-header">Credit Used</th>
                      <th className="table-header">Utilization</th>
                      <th className="table-header">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mockEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                            <div className="text-sm text-gray-500">{employee.employeeId}</div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-gray-900">{employee.department}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">₦{employee.creditLimit.toLocaleString()}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">₦{employee.creditUsed.toLocaleString()}</span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-primary-600 h-2 rounded-full" 
                                style={{ width: `${(employee.creditUsed / employee.creditLimit) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {((employee.creditUsed / employee.creditLimit) * 100).toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            employee.status === 'active' ? 'bg-green-100 text-green-800' :
                            employee.status === 'suspended' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Comprehensive financial reports and department analysis</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button 
            onClick={() => handleExport(selectedReport)}
            className="btn-primary"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="card">
        <div className="flex space-x-1">
          <button
            onClick={() => setSelectedReport('financial')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedReport === 'financial'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Financial Summary
          </button>
          <button
            onClick={() => setSelectedReport('department')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedReport === 'department'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Department Analysis
          </button>
          <button
            onClick={() => setSelectedReport('employee')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedReport === 'employee'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-700 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Employee Performance
          </button>
        </div>
      </div>

      {/* Report Content */}
      {getReportContent()}
    </div>
  );
};

export default Reports;
